import React from "react";
import {
    Box,
    Typography,
    Stack,
    Chip,
    Divider,
    Breadcrumbs,
    Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CustomButton from "../common/CustomButton";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";

/**
 * CandidateMatchesHeader
 *
 * Props:
 *  jdTitle        – string  – job description title shown in the pill
 *  stats          – { total, strong, moderate, weak }
 *  inviteLoading  – bool
 *  onInviteAll    – fn
 *  onRefresh      – fn
 *  lastSynced     – string  – e.g. "2 min ago"  (optional)
 */
const CandidateMatchesHeader = ({
    jdTitle = "",
    stats = { total: 0, strong: 0, moderate: 0, weak: 0 },
    inviteLoading = false,
    onInviteAll,
    onRefresh,
    lastSynced = "",
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // ── stat pills config
    const pills = [
        { label: `${stats.strong} strong`,   dot: theme.palette.success.main },
        { label: `${stats.moderate} moderate`, dot: theme.palette.warning.main },
        { label: `${stats.weak} weak`,        dot: theme.palette.error.main },
    ];

    return (
        <Box sx={{ pb: 0 }}>
            {/* breadcrumb */}
            <Breadcrumbs
                separator={<NavigateNextIcon sx={{ fontSize: 13, color: "text.disabled" }} />}
                sx={{ mb: 1.25 }}
            >
                <Typography
                    component={Link}
                    to="/recruiter/dashboard"
                    fontSize="0.75rem"
                    color="text.secondary"
                    sx={{ textDecoration: "none", "&:hover": { color: "text.primary" } }}
                >
                    Dashboard
                </Typography>
                <Typography
                    component={Link}
                    to="/recruiter/landing"
                    fontSize="0.75rem"
                    color="text.secondary"
                    sx={{ textDecoration: "none", "&:hover": { color: "text.primary" } }}
                >
                    Job Descriptions
                </Typography>
                <Typography fontSize="0.75rem" color="text.primary" fontWeight={500}>
                    Candidate Matches
                </Typography>
            </Breadcrumbs>

            {/* main header row */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                    mb: 1.75,
                }}
            >
                {/* left – icon + title + sub */}
                <Stack direction="row" alignItems="center" spacing={1.75}>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <PeopleAltOutlinedIcon
                            sx={{ fontSize: 22, color: theme.palette.primary.main }}
                        />
                    </Box>

                    <Box>
                        <Typography
                            fontSize="1.25rem"
                            fontWeight={700}
                            lineHeight={1.2}
                            color="text.primary"
                        >
                            Candidate Matches
                        </Typography>

                        <Stack direction="row" alignItems="center" spacing={1} mt={0.4} flexWrap="wrap">
                            {/* JD pill */}
                            {jdTitle && (
                                <Chip
                                    icon={
                                        <InsertDriveFileOutlinedIcon
                                            sx={{ fontSize: "13px !important", ml: "6px !important" }}
                                        />
                                    }
                                    label={jdTitle}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        fontSize: "0.72rem",
                                        fontWeight: 500,
                                        height: 22,
                                        borderRadius: "20px",
                                        color: "text.secondary",
                                        borderColor: "divider",
                                        bgcolor: "action.hover",
                                    }}
                                />
                            )}

                            {/* last synced */}
                            {lastSynced && (
                                <Typography fontSize="0.68rem" color="text.disabled">
                                    Last synced {lastSynced}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </Stack>

                {/* right – stat pills + actions */}
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    {/* stat pills */}
                    {pills.map(({ label, dot }) => (
                        <Chip
                            key={label}
                            size="small"
                            label={
                                <Stack direction="row" alignItems="center" spacing={0.6}>
                                    <Box
                                        sx={{
                                            width: 7,
                                            height: 7,
                                            borderRadius: "50%",
                                            bgcolor: dot,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <span>{label}</span>
                                </Stack>
                            }
                            sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                height: 26,
                                borderRadius: "20px",
                                bgcolor: "background.paper",
                                border: `1px solid ${theme.palette.divider}`,
                                color: "text.secondary",
                                "& .MuiChip-label": { px: 1.25 },
                            }}
                        />
                    ))}

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    {/* refresh */}
                    <Tooltip title="Refresh results">
                        <span>
                            <CustomButton
                                variant="outlined"
                                size="small"
                                startIcon={<RefreshIcon />}
                                onClick={onRefresh}
                                sx={{
                                    height: 34,
                                    fontSize: "0.78rem",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    borderRadius: 1.5,
                                }}
                            >
                                Refresh
                            </CustomButton>
                        </span>
                    </Tooltip>

                    {/* invite all */}
                    <CustomButton
                        variant="contained"
                        size="small"
                        startIcon={<SendIcon />}
                        loading={inviteLoading}
                        loadingText="Inviting…"
                        onClick={onInviteAll}
                        sx={{
                            height: 34,
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            textTransform: "none",
                            borderRadius: 1.5,
                        }}
                    >
                        Invite All
                    </CustomButton>
                </Stack>
            </Box>

            <Divider />
        </Box>
    );
};

export default CandidateMatchesHeader;